<script setup lang="ts">
import {
  instanceOfQaEquipmentRtSystemDto,
  instanceOfQaEquipmentCatalogDto,
  MedicalSystemCatalogDto,
  MedicalSystemRtSystemDto,
  QaEquipmentCatalogDto,
  QaEquipmentRtSystemDto,
} from '@/api/generator';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types.ts';
import DeviceCalibrationState from '@/components/deviceStatus/DeviceCalibrationState.vue';
import DeviceErrorInline from '@/components/deviceStatus/DeviceErrorInline.vue';
import DeviceLicenseInline from '@/components/deviceStatus/DeviceLicenseInline.vue';
import DeviceState from '@/components/deviceStatus/DeviceState.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { mainNavigationQaEquipment, mainNavigationMedicalSystems } from '@/components/icons/icons';
import StateOfUseBadge from '@/components/stateOfUse/StateOfUseBadge.vue';
import { isIOTDevice, isSimulator } from '@/utils/rtSystem';
import { I18nKey } from '@/utils/typings';
import { mdiMinus } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { RTSystemTileSelectionType } from './RTSystemTileSelectionType';
import Simulator from '@/components/Simulator/Simulator.vue';
import BaseImage from '../images/BaseImage.vue';
import { createOverviewImageProps } from '@/components/images/presets';

interface RTSystemTileProps {
  selected?: boolean;
  removable?: boolean;
  enabled?: boolean;
  item:
    | MedicalSystemRtSystemDto
    | QaEquipmentRtSystemDto
    | MedicalSystemCatalogDto
    | QaEquipmentCatalogDto;
  selectionType?: RTSystemTileSelectionType;
  dontShowBadges?: boolean;
  dontShowDeviceState?: boolean;
  showTrackItAlias?: boolean;
}

const props = defineProps<RTSystemTileProps>();
const emits = defineEmits<{
  remove: [];
}>();

const isEnabled = ref(!props.item.state || props.item.state === 'Enabled');

const showIotStateAndBattery =
  props.item &&
  isIOTDevice(props.item) &&
  props.item.isAvailable &&
  props.item.registrationState === 'Registered' &&
  (props.item.state === 'Enabled' || props.item.state === 'InEditing');

const typedKeys = ref('');

function handleKeyDown(event: KeyboardEvent) {
  if (!event || !event.key) {
    return;
  }
  typedKeys.value += event.key.toLowerCase();

  if (typedKeys.value.endsWith('--tileactive')) {
    isEnabled.value = true;
    typedKeys.value = '';
  }

  if (typedKeys.value.length > 12) {
    typedKeys.value = typedKeys.value.slice(-12);
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
});

const { t } = useTranslation();
const mode = useFormMode();

const placeholderIcon = computed(() =>
  instanceOfQaEquipmentRtSystemDto(props.item) || instanceOfQaEquipmentCatalogDto(props.item)
    ? mainNavigationQaEquipment
    : mainNavigationMedicalSystems,
);

const serialNumberOrId = computed(() => {
  if ('serialNumber' in props.item && props.item.serialNumber) {
    return `SN: ${props.item.serialNumber}`;
  } else if ('identification' in props.item && props.item.identification !== '') {
    return `${t('Identification')}: ${props.item.identification}`;
  }
  return undefined;
});

const refNumber = computed(() => {
  if ('typeNumber' in props.item && props.item.typeNumber) {
    return `REF: ${props.item.typeNumber}`;
  }
  return undefined;
});

const submodel = computed(() => {
  return 'subModel' in props.item ? props.item.subModel : undefined;
});

const trackItAlias = computed(() =>
  props.showTrackItAlias && 'trackItAlias' in props.item ? props.item.trackItAlias : undefined,
);

const subline = computed(() => {
  let returnString = '';
  const isQaEquipment =
    instanceOfQaEquipmentRtSystemDto(props.item) || instanceOfQaEquipmentCatalogDto(props.item);
  if (props.item.subCategory) {
    if (isQaEquipment) {
      returnString += `${t(('QaEquipmentSubCategoryEnum.' + props.item.subCategory) as I18nKey)} | `;
    } else {
      returnString += `${t(('RtSystemCategoryEnum.' + props.item.category) as I18nKey)} | ${t(('MedicalSystemSubCategoryEnum.' + props.item.subCategory) as I18nKey)} | `;
    }
  }

  returnString += `${t(('RtSystemManufacturerEnum.' + props.item.manufacturer) as I18nKey)} | ${
    isQaEquipment ? props.item.model : t(('RtSystemModelEnum.' + props.item.model) as I18nKey)
  }`;

  if (submodel.value) {
    returnString += isQaEquipment
      ? ` | ${submodel.value}`
      : ` | ${t(('MedicalSystemSubModelEnum.' + submodel.value) as I18nKey)}`;
  }

  return returnString;
});

const contextMenuActions: ContextMenuAction[] = [
  {
    text: t('Remove'),
    divider: false,
    icon: mdiMinus,
    hidden: !props.removable,
    disabled: !props.removable,
    callback: () => {
      emits('remove');
    },
  },
];

// Use overview preset but preserve original aspect ratio (no cropping)
const tileImageProps = computed(() =>
  createOverviewImageProps({
    fallbackIcon: placeholderIcon.value,
    cover: false, // Keep original aspect ratio instead of cropping to square
    mobileWidth: 80,
    desktopWidth: 120,
  }),
);
</script>

<template>
  <v-card
    :id="item.id"
    class="system-tile"
    :class="{
      selected: selected,
      'text-primary-darken-2': isEnabled,
    }"
    :aria-label="`${item.name} ${item.iuid}`"
    elevation="2"
  >
    <v-card-item>
      <template #prepend>
        <BaseImage
          v-bind="tileImageProps"
          :pictureId="props.item.imageId"
          :tooltip="item.name ?? undefined"
        />
      </template>

      <div class="listElement__txt-container">
        <StateOfUseBadge v-if="!dontShowBadges" :value="item.state" />

        <template v-if="isIOTDevice(item) && !dontShowBadges">
          <div class="d-flex flex-column ga-1 mb-1">
            <DeviceCalibrationState :deviceId="item.id" />
            <DeviceErrorInline :device="item" :withDetailsLink="true" />
            <DeviceLicenseInline :device="item" />
          </div>
        </template>

        <h5 class="text-caption text-primary-lighten-1 font-weight-medium">
          {{ subline }}
        </h5>
        <div
          :style="{
            display: 'flex',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
          }"
        >
          <h5 class="system-label w-auto">{{ item.name }}</h5>

          <h5 class="font-weight-regular system-label w-auto">| {{ item.iuid }}</h5>
        </div>
        <div class="text-body-2 font-weight-regular">
          <Simulator
            :unicheck="
              (instanceOfQaEquipmentRtSystemDto(item) || instanceOfQaEquipmentCatalogDto(item)) &&
              isIOTDevice(item) &&
              isSimulator(item)
                ? item
                : undefined
            "
          >
            <template #default="{ toggle }">
              <p @click="toggle()">
                <template v-if="refNumber">{{ refNumber }}</template>
                <template v-if="serialNumberOrId && refNumber"> | </template>
                <template v-if="serialNumberOrId">{{ serialNumberOrId }}</template>
              </p>
            </template>
          </Simulator>

          <p v-if="trackItAlias">{{ $t('TrackItAlias' as I18nKey) }}: {{ trackItAlias }}</p>

          <DeviceState
            v-if="showIotStateAndBattery && !dontShowDeviceState"
            :deviceId="item.id"
            orientation="horizontal"
          />
        </div>
      </div>

      <template #append>
        <ContextMenu v-if="mode !== 'details'" :actions="contextMenuActions" />
      </template>
    </v-card-item>
  </v-card>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';

h2 {
  font-size: 16px;
  color: black;
  margin-bottom: 8px;
}

.subline {
  margin-bottom: 30px;
}

.system-tile {
  justify-content: stretch;
  border-radius: 4px;
  padding: 8px;
}

.system-tile--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.listElement:hover,
.selected {
  background-color: rgb(var(--v-theme-secondary-lighten-4));
}

:deep(.listElement__img) {
  width: 76px;
  height: 76px;
}

.listElement__txt-container {
  display: flex;
  flex-direction: column;
  text-align: left;
  align-self: flex-start;
  p,
  h5,
  h4 {
    margin-bottom: 4px !important;
    margin-left: 4px !important;
  }
}

:deep(.stateOfUseBadge .v-badge__wrapper) {
  margin: 0px !important;
}

:deep(.v-card-item) {
  padding: 0px;
}

:deep(.v-card-item__prepend) {
  align-self: flex-start;
}

:deep(.v-card-item__content) {
  align-self: flex-start;
}

:deep(.license-alert) {
  margin-top: 4px !important;
}
.system-label {
  white-space: nowrap; /* Prevents text from wrapping to a new line */
  overflow: hidden; /* Hides any overflow content */
  text-overflow: ellipsis; /* Adds '...' at the end if text overflows */
  width: 100%;
  line-height: 24px;
}

:deep(.v-btn__content) {
  justify-content: stretch;
}
</style>
